import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export const useImportCommunityTopic = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (communityTopicId: string) => {
      if (!user) throw new Error("User must be logged in");

      // Fetch the community topic
      const { data: communityTopic, error: fetchError } = await supabase
        .from('community_topics')
        .select('*')
        .eq('id', communityTopicId)
        .single();

      if (fetchError) throw fetchError;
      if (!communityTopic) throw new Error("Topic not found");

      // Create new topic in user's topics
      const { data: newTopic, error: topicError } = await supabase
        .from('topics')
        .insert({
          user_id: user.id,
          title: communityTopic.title,
          course_outline: communityTopic.course_outline,
          outline_accepted_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (topicError) throw topicError;

      // Create initial session for the topic
      const { error: sessionError } = await supabase
        .from('sessions')
        .insert({
          user_id: user.id,
          topic_id: newTopic.id,
          title: communityTopic.title,
          title_source: 'default',
        });

      if (sessionError) throw sessionError;

      // Create default study note
      const { error: noteError } = await supabase
        .from('study_notes')
        .insert({
          user_id: user.id,
          topic_id: newTopic.id,
          title: `Study Notes for ${communityTopic.title}`,
          content: [{"type": "paragraph", "content": [{"type": "text", "text": "Welcome to your study notes! Start writing here..."}]}],
        });

      if (noteError) throw noteError;

      // If community topic has lessons, copy them
      if (communityTopic.lessons && Array.isArray(communityTopic.lessons)) {
        const normalizeLessonType = (type?: string) => (type === 'quiz' ? 'quiz' : 'lesson');
        
        const lessonsToInsert = communityTopic.lessons.map((lesson: any, index: number) => ({
          user_id: user.id,
          topic_id: newTopic.id,
          title: lesson.title || `Lesson ${index + 1}`,
          lesson_number: index + 1,
          type: normalizeLessonType(lesson.type),
          description: lesson.description || null,
          status: 'generated',
          duration_minutes: lesson.duration_minutes != null ? Math.round(Number(lesson.duration_minutes)) : null,
          learning_objectives: lesson.learning_objectives || null,
          source_references: lesson.source_references || null,
          foundation_data: lesson.lesson_context_prompt ? {
            lesson_context_prompt: lesson.lesson_context_prompt,
            learner_level: lesson.learner_level,
            prerequisites: lesson.prerequisites,
          } : null,
          slides_generated: lesson.slides?.items ? Object.keys(lesson.slides.items).length : 0,
        }));

        const { data: createdLessons, error: lessonsError } = await supabase
          .from('topic_lessons')
          .insert(lessonsToInsert)
          .select();

        if (lessonsError) throw lessonsError;

        // Now create slides for each lesson
        const allSlides: any[] = [];

        createdLessons?.forEach((createdLesson, index) => {
          const communityLesson = communityTopic.lessons[index];
          const slidesItems = communityLesson.slides?.items;
          
          if (slidesItems) {
            let orderCounter = 1;
            Object.entries(slidesItems).forEach(([slideId, slide]: [string, any]) => {
              const slideOrder = typeof slide.sequence === 'number' && slide.sequence > 0 
                ? slide.sequence 
                : orderCounter++;
              
              allSlides.push({
                lesson_id: createdLesson.id,
                topic_id: newTopic.id,
                user_id: user.id,
                slide_id: slideId,
                slide_order: slideOrder,
                slide_type: slide.type || 'learn',
                title: slide.title || `Slide ${slideOrder}`,
                section: slide.section || 'learn',
                content: slide.content || {},
                structured: slide.structured || null,
                tutor_prompt: slide.tutor_prompt || null,
                duration_minutes: slide.duration_minutes != null ? Math.round(Number(slide.duration_minutes)) : null,
                status: 'completed',
              });
            });
          }
        });

        // Batch insert all slides
        if (allSlides.length > 0) {
          const { error: slidesError } = await supabase
            .from('lesson_slides')
            .insert(allSlides);
            
          if (slidesError) throw slidesError;
        }
      }

      // Increment import count (non-blocking, via secure edge function)
      supabase.functions
        .invoke('community-topic-metrics', {
          body: { topicId: communityTopicId, action: 'import' },
        })
        .catch((err) => console.warn('Failed to increment import count:', err));

      return newTopic;
    },
    onSuccess: (newTopic) => {
      queryClient.invalidateQueries({ queryKey: ['topics'] });
      queryClient.invalidateQueries({ queryKey: ['community-topics'] });
      toast.success("Topic added to your workspace!");
      navigate(`/topic/${newTopic.id}`);
    },
    onError: (error) => {
      console.error("Error adding topic:", error);
      toast.error("Failed to add topic. Please try again.");
    },
  });
};
